import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion, useReducedMotion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import styles from './Empty.module.css';

const empty = cva(styles.base, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type DivMotionSafeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart' | 'title'>;

interface EmptyProps extends DivMotionSafeProps, VariantProps<typeof empty> {
  /** Custom image/icon element (replaces default inbox icon) */
  image?: React.ReactNode;
  /** Primary title text, rendered prominently above the description */
  title?: React.ReactNode;
  /** Secondary description text below the title/image */
  description?: React.ReactNode;
  /** Action buttons below the description (e.g. a Button to create something) */
  children?: React.ReactNode;
  /** Visual size of the empty state container */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Empty — placeholder for empty states.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | image | ReactNode | Inbox icon | Custom image/icon |
 * | title | ReactNode | — | Prominent title above description |
 * | description | ReactNode | 'No data' | Secondary text below title |
 * | children | ReactNode | — | Action slot (e.g. a button) |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Visual size |
 *
 * @example
 * <Empty />
 * <Empty title="No documents" description="Create one to get started">
 *   <Button variant="primary">New Document</Button>
 * </Empty>
 * <Empty image={<FileX size={48} />} title="File not found" size="sm" />
 */
export function Empty({ image, title, description = 'No data', children, size = 'md', className, style, ...props }: EmptyProps) {
  const safeProps = props;
  const shouldReduce = useReducedMotion() ?? false;
  const defaultImage = <Icon name="inbox" size={48} className={styles.defaultIcon} />;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={shouldReduce ? { duration: 0 } : { duration: duration.slow / 1000, ease: 'easeOut' }}
      className={empty({ size, className })}
      style={style}
      {...safeProps}
    >
      <div>{image ?? defaultImage}</div>
      {title && (
        <div className={styles.title}>
          {title}
        </div>
      )}
      {description !== false && (
        <div className={styles.description}>
          {description}
        </div>
      )}
      {children && <div className={styles.actions}>{children}</div>}
    </motion.div>
  );
}
